import type {
  ConnectedRepository,
  RecentAnalysis,
  DashboardStats,
  UserProfile,
  GuidanceState,
} from '../types/dashboard';

import {
  DEFAULT_GUIDANCE_PREFERENCES,
  DEFAULT_GUIDANCE_INTERACTIONS,
  DEFAULT_USER_ACTIVITY_METRICS,
} from '../types/dashboard';

import axios from 'axios';
import api from './authenticatedApi';
import type { PaginatedResponse } from '../types/api/pagination';
import { logger } from '../utils/logger';
import {
  transformAnalyzedRepository,
  transformRepository,
  transformRepositoryToAnalysis,
  transformDashboardData,
  type RawAnalyzedRepository,
} from './DashboardService.transformers';

export interface DashboardData {
  user: UserProfile;
  guidance: GuidanceState;
  repositories: ConnectedRepository[];
  recentAnalyses: RecentAnalysis[];
  stats: DashboardStats;
}

/** Partial user data for mock/demo scenarios */
interface DemoUser {
  id?: string;
  firstName?: string;
  primaryEmailAddress?: { emailAddress?: string };
}

class DashboardService {
  /**
   * Check if we're in demo mode from multiple sources
   */
  private isDemoMode(): boolean {
    if (window.location.pathname.includes('/demo')) {
      return true;
    }

    if (sessionStorage.getItem('demoMode') === 'true') {
      return true;
    }

    if (sessionStorage.getItem('flowType') === 'demo') {
      return true;
    }

    if (window.location.pathname === '/setup' && sessionStorage.getItem('flowType') === 'demo') {
      return true;
    }

    return false;
  }

  /**
   * Fetch complete dashboard data for the authenticated user
   */
  async getDashboardData(): Promise<DashboardData> {
    if (this.isDemoMode()) {
      return this.createMockDashboardData(null, true);
    }

    try {
      const response = await api.get('/dashboard');

      return transformDashboardData(response.data);
    } catch (error: unknown) {
      logger.error('Dashboard data fetch failed:', { error });

      if (axios.isAxiosError(error) && error.response?.status === 401) {
        throw new Error('Authentication required');
      }

      if (axios.isAxiosError(error)) {
        const data = error.response?.data as Record<string, unknown> | undefined;
        const message =
          (typeof data?.message === 'string' ? data.message : null) ||
          error.message ||
          'Failed to fetch dashboard data';
        throw new Error(message);
      }

      const message = error instanceof Error ? error.message : 'Failed to fetch dashboard data';
      throw new Error(message);
    }
  }

  /**
   * Fetch connected repositories for the user
   */
  async getConnectedRepositories(): Promise<ConnectedRepository[]> {
    try {
      const response = await api.get('/repositories/connected');
      return response.data.repositories.map(transformRepository);
    } catch (error) {
      logger.error('Repository fetch failed:', { error });
      return [];
    }
  }

  /**
   * Fetch one server-paginated page of analyzed repositories. Server applies
   * status filter + sort + pagination — the dashboard renders the page as-is
   * (no client-side post-filter or post-sort). Client uses 'devScore' as the
   * UI sort key; map to the underlying 'analysisScore' column the server knows.
   */
  async getAnalyzedRepositories(params: {
    page: number;
    perPage: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<
    PaginatedResponse<ConnectedRepository> & {
      counts: { all: number; analyzing: number; error: number; issues: number; healthy: number };
    }
  > {
    const serverSortBy = params.sortBy === 'devScore' ? 'analysisScore' : params.sortBy;
    const emptyCounts = { all: 0, analyzing: 0, error: 0, issues: 0, healthy: 0 };
    try {
      const response = await api.get<
        PaginatedResponse<RawAnalyzedRepository> & { counts: typeof emptyCounts }
      >('/analysis/repositories', {
        params: {
          page: params.page,
          perPage: params.perPage,
          ...(params.status && params.status !== 'all' ? { status: params.status } : {}),
          ...(serverSortBy ? { sortBy: serverSortBy } : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
          ...(params.search ? { search: params.search } : {}),
        },
      });
      return {
        data: response.data.data.map(transformAnalyzedRepository),
        meta: response.data.meta,
        counts: response.data.counts ?? emptyCounts,
      };
    } catch (error) {
      logger.error('Analyzed repositories fetch failed:', { error });
      return {
        data: [],
        meta: {
          total: 0,
          lastPage: 1,
          currentPage: params.page,
          perPage: params.perPage,
          prev: null,
          next: null,
        },
        counts: emptyCounts,
      };
    }
  }

  /**
   * Fetch recent analysis results for the user
   * Gets repositories sorted by last accessed date
   */
  async getRecentAnalyses(perPage: number = 10): Promise<RecentAnalysis[]> {
    try {
      const response = await api.get<PaginatedResponse<RawAnalyzedRepository>>(
        '/analysis/repositories',
        { params: { page: 1, perPage } },
      );
      return response.data.data
        .map((repo) => transformRepositoryToAnalysis(repo))
        .sort((a, b) => (b.completedAt?.getTime() || 0) - (a.completedAt?.getTime() || 0));
    } catch (error) {
      logger.error('Analysis fetch failed:', { error });
      return [];
    }
  }

  /**
   * Fetch dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      logger.error('Stats fetch failed:', { error });
      return {
        totalRepositories: 0,
        totalAnalyses: 0,
        averageScore: 0,
        issuesResolved: 0,
      };
    }
  }

  /**
   * Update user guidance state
   */
  async updateGuidanceState(guidanceState: Partial<GuidanceState>): Promise<void> {
    try {
      await api.patch('/user/guidance', guidanceState);
    } catch (error) {
      logger.error('Failed to update guidance state:', { error });
    }
  }

  /**
   * Create mock data for development/demo purposes
   */
  createMockDashboardData(user: DemoUser | null, isFirstVisit: boolean = false): DashboardData {
    const now = new Date();
    const joinedAt = isFirstVisit ? now : new Date(now.getTime() - 25 * 60 * 60 * 1000);

    return {
      user: {
        id: user?.id || 'demo-user',
        name: user?.firstName || 'Demo User',
        email: user?.primaryEmailAddress?.emailAddress || 'demo@example.com',
        joinedAt,
        isFirstVisit,
        guidanceCompleted: false,
        lastActiveDate: now,
        activityMetrics: DEFAULT_USER_ACTIVITY_METRICS,
      },
      guidance: {
        enabled: false,
        currentStep: 0,
        completedSteps: [],
        dismissed: false,
        lastShown: now,
        completionRate: 0,
        userPreferences: DEFAULT_GUIDANCE_PREFERENCES,
        interactions: DEFAULT_GUIDANCE_INTERACTIONS,
      },
      repositories: [],
      recentAnalyses: [],
      stats: {
        totalRepositories: 0,
        totalAnalyses: 0,
        averageScore: 0,
        issuesResolved: 0,
      },
    };
  }
}

export const dashboardService = new DashboardService();
